<script setup>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Tag from "primevue/tag";
import Dropdown from "primevue/dropdown";
import { ref, watch } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { debounce } from "../../../utils/debounce";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";

const props = defineProps([
    'serviceCategories',
    'companies',
    'filters',
    'flash',
]);

const confirm = useConfirm();
const toast = useToast();
const search = ref(props.filters.search);
const company_id = ref(props.filters.company_id);

const updateSearch = debounce(() => {
    const params = {
        search: search.value,
    };

    if (Inertia.page.props.auth.user.role === 'super_admin') {
        params.company_id = company_id.value;
    }

    Inertia.get('/management/service-categories', params, { preserveState: true, replace: true });
}, 500);

watch([search, company_id], () => {
    updateSearch();
});

const getStatusSeverity = (isActive) => {
    return isActive ? 'success' : 'danger';
};

const getStatusLabel = (isActive) => {
    return isActive ? 'Aktiv' : 'Inaktiv';
};

const confirmDelete = (category) => {
    confirm.require({
        message: `Sind Sie sicher, dass Sie die Kategorie "${category.name}" löschen möchten?`,
        header: 'Löschen bestätigen',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Löschen',
        rejectLabel: 'Abbrechen',
        accept: () => {
            Inertia.delete(`/management/service-categories/${category.uuid}`, {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Erfolgreich',
                        detail: 'Kategorie wurde gelöscht.',
                        life: 5000,
                    });
                }
            });
        }
    });
};

</script>

<template>
    <Layout :flash="flash">
        <Head title="Dienstleistungskategorien" />

        <div class="space-y-6">
            <div class="flex justify-between items-center">
                <h1 class="text-2xl font-bold text-gray-800">Dienstleistungskategorien</h1>
                <Link href="/management/service-categories/create">
                    <Button label="Hinzufügen" icon="pi pi-plus" severity="success" class="!px-4 !py-2" />
                </Link>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div class="p-4 border-b border-gray-50 flex flex-col md:flex-row gap-4 items-center bg-gray-50/50">
                    <IconField iconPosition="left" class="w-full max-w-md">
                        <InputIcon class="pi pi-search"></InputIcon>
                        <InputText v-model="search"
                                   class="w-full"
                                   placeholder="Suche nach Name oder Beschreibung..." />
                    </IconField>
                    
                    <Dropdown v-if="$page.props.auth.user.role === 'super_admin'"
                              v-model="company_id" 
                              :options="companies" 
                              optionLabel="name" 
                              optionValue="id" 
                              placeholder="Unternehmen filtern" 
                              class="w-full md:w-64" 
                              showClear />
                </div>

                <DataTable :value="serviceCategories.data" 
                           :rows="20" 
                           dataKey="id"
                           responsiveLayout="scroll"
                           class="p-datatable-sm">
                    <Column field="name" header="Name" sortable class="font-semibold"></Column>
                    <Column v-if="$page.props.auth.user.role === 'super_admin'" field="company.name" header="Pursue" sortable></Column>
                    <Column header="Dienstleistungen">
                        <template #body="slotProps">
                            <div class="flex flex-wrap gap-1">
                                <Tag v-for="service in slotProps.data.services" 
                                     :key="service.id" 
                                     :value="service.name" 
                                     severity="info" 
                                     class="text-[10px]" />
                                <span v-if="!slotProps.data.services?.length" class="text-gray-400 italic text-xs">Keine Zuweisung</span>
                            </div>
                        </template>
                    </Column>
                    <Column field="sort_order" header="Sortierung" sortable></Column>
                    <Column field="is_active" header="Status" sortable>
                        <template #body="slotProps">
                            <Tag :value="getStatusLabel(slotProps.data.is_active)" 
                                 :severity="getStatusSeverity(slotProps.data.is_active)" />
                        </template>
                    </Column>
                    <Column header="Aktionen" headerStyle="width: 8rem; text-align: center" bodyStyle="text-align: center">
                        <template #body="slotProps">
                            <div class="flex justify-center gap-3">
                                <Link :href="route('management.service-categories.edit', slotProps.data.uuid)">
                                    <Button icon="pi pi-pencil" severity="info" outlined class="!p-2" />
                                </Link>
                                <Button icon="pi pi-trash" severity="danger" outlined class="!p-2" @click.stop="confirmDelete(slotProps.data)" />
                            </div>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="py-12 text-center">
                            <i class="pi pi-tag text-4xl text-gray-200 mb-4" />
                            <p class="text-gray-400">Keine Kategorien gefunden.</p>
                        </div>
                    </template>
                </DataTable>

                <!-- Simple Pagination Info -->
                <div class="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
                    <div>
                        Zeige {{ serviceCategories.from }} bis {{ serviceCategories.to }} von {{ serviceCategories.total }} Einträgen
                    </div>
                    <div class="flex gap-2">
                        <Link v-for="link in serviceCategories.links" 
                              :key="link.label"
                              :href="link.url || '#'"
                              v-html="link.label"
                              :class="[
                                'px-3 py-1 rounded border transition-colors',
                                link.active ? 'bg-black text-[#FFF053] border-black' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                                !link.url ? 'opacity-50 cursor-not-allowed' : ''
                              ]" />
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
:deep(.p-datatable-thead > tr > th) {
    background: transparent;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1rem;
}

:deep(.p-datatable-tbody > tr > td) {
    padding: 1rem;
    border-bottom: 1px solid #f8fafc;
}

:deep(.p-datatable-tbody > tr:hover) {
    background-color: #fcfcfd;
}
</style>
